export interface IFieldError {
  field: string;
  message: string;
  value?: unknown;
  code?: string;
}

interface IErrorEnvelope {
  code: string;
  httpCode: number;
  requestId: string;
  timestamp: string;
  path: string;
  method: string;
  message?: string;
  details?: unknown;
}

const isFieldError = (value: unknown): value is IFieldError =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as IFieldError).field === 'string' &&
  typeof (value as IFieldError).message === 'string';

// The server has three ways of saying "this field is wrong"; reading only the first drops the rest onto no screen at all.
const readFieldErrors = (details: unknown, code: string, message?: string): IFieldError[] => {
  if (Array.isArray(details)) {
    const direct = details.filter(isFieldError);
    if (direct.length > 0) return direct;
  }

  const takenField = /\.([a-z]+)_taken$/.exec(code)?.[1];
  if (takenField && (typeof details !== 'object' || details === null || !(takenField in details))) {
    return [{ field: takenField, message: message ?? `That ${takenField} is already taken` }];
  }

  if (typeof details !== 'object' || details === null) return [];
  const record = details as Record<string, unknown>;

  if (Array.isArray(record.issues)) {
    return record.issues.flatMap((issue) => {
      if (typeof issue !== 'object' || issue === null) return [];
      const { path, message } = issue as { path?: unknown; message?: unknown };
      const field = Array.isArray(path) ? path.join('.') : undefined;
      if (!field || typeof message !== 'string') return [];
      return [{ field, message }];
    });
  }

  const conflicted = Object.keys(record).find((key) => code.endsWith(`.${key}_taken`));
  if (conflicted) {
    return [
      {
        field: conflicted,
        message: `That ${conflicted} is already taken`,
        value: record[conflicted],
      },
    ];
  }

  return [];
};

export const SIGNED_OUT_MESSAGE = 'You were signed out — sign in again';

// `auth.invalid_credentials` is absent on purpose: a mistyped password is the user's, not the session's.
const SIGNED_OUT_CODES: ReadonlySet<string> = new Set([
  'common.unauthorized',
  'auth.session_revoked',
  'auth.session_expired',
  'auth.session_not_found',
  'auth.invalid_refresh_token',
]);

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string;
  readonly fieldErrors: readonly IFieldError[];
  readonly rawDetails: unknown;
  /** The session itself was refused; the client is already on its way to sign-in. */
  readonly signedOut: boolean;

  constructor(envelope: IErrorEnvelope, status: number) {
    const signedOut =
      SIGNED_OUT_CODES.has(envelope.code) || (envelope.code === 'UNPARSEABLE' && status === 401);
    super(signedOut ? SIGNED_OUT_MESSAGE : (envelope.message ?? envelope.code ?? 'Request failed'));
    this.name = 'ApiError';
    this.code = envelope.code ?? 'UNKNOWN';
    this.status = envelope.httpCode ?? status;
    this.requestId = envelope.requestId ?? '';
    this.signedOut = signedOut;

    this.fieldErrors = signedOut
      ? []
      : readFieldErrors(envelope.details, envelope.code, envelope.message);
    this.rawDetails = signedOut || this.fieldErrors.length > 0 ? undefined : envelope.details;
  }

  forField(field: string): IFieldError[] {
    return this.fieldErrors.filter((error) => error.field === field);
  }

  unattached(renderedFields: readonly string[]): IFieldError[] {
    const known = new Set(renderedFields);
    return this.fieldErrors.filter((error) => !known.has(error.field));
  }
}

/** One key of a refusal's `details` — `waiting`, `open`, `liveVersion` — when it is a number. */
export const detailNumber = (error: unknown, key: string): number | undefined => {
  if (!(error instanceof ApiError)) return undefined;
  const details = error.rawDetails;
  if (typeof details !== 'object' || details === null) return undefined;
  const value = (details as Record<string, unknown>)[key];
  return typeof value === 'number' ? value : undefined;
};

export const isApiCode = (error: unknown, code: string): error is ApiError =>
  error instanceof ApiError && error.code === code;

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Could not reach the server');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

// The body is already parsed here: reading the Response a second time throws.
export const toApiError = (parsed: unknown, response: Response): ApiError => {
  const envelope =
    typeof parsed === 'object' && parsed !== null && 'code' in parsed
      ? (parsed as IErrorEnvelope)
      : {
          code: 'UNPARSEABLE',
          httpCode: response.status,
          requestId: '',
          timestamp: new Date().toISOString(),
          path: '',
          method: '',
          message: response.statusText || `HTTP ${response.status}`,
        };
  return new ApiError(envelope, response.status);
};

const detailValue = (value: unknown): string =>
  typeof value === 'string' ? value : value === undefined ? '' : JSON.stringify(value);

const ID_LIKE = /^[a-z]{2,5}_[A-Za-z0-9]{6,}$/;

// Any shape of `details` no reader above knows, one line each: a reason must never render as silence.
export const detailLines = (details: unknown): string[] => {
  if (details === null || details === undefined) return [];
  if (Array.isArray(details)) {
    return details.flatMap((item) =>
      typeof item === 'object' && item !== null ? detailLines(item) : [detailValue(item)],
    );
  }
  if (typeof details === 'object') {
    return Object.entries(details as Record<string, unknown>)
      .filter(([, value]) => !(typeof value === 'string' && ID_LIKE.test(value)))
      .map(([key, value]) => `${key}: ${detailValue(value)}`);
  }
  return [detailValue(details)];
};
